/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { css } from "@emotion/react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Row, RowProps } from "../Layouts";
import { arrayShallowEqual } from "../@utils/arrayShallowEqual";

const camelize = (str: string) => {
  const arr = str.split("-");
  const capital = arr.map((item, index) =>
    index ? item.charAt(0).toUpperCase() + item.slice(1).toLowerCase() : item,
  );
  const capitalString = capital.join("");
  return capitalString;
};

const dataid = "data-sort-area-item-index";
const dataId = camelize(dataid.replace("data-", ""));

const INTERACTIVE_SELECTOR = "button, a, input, label, textarea";

export interface SortAreaProps extends Omit<RowProps, "onChange"> {
  /**
   * このコンポーネントの子要素はkey属性を必ず持っている必要があります。
   * keyはuniqueでなければなりません。決して安直なmapのindexなどを渡さないようにしてください。
   * また子コンポーネントは任意のpropsを受け取れるようになっている必要があります。
   */
  children: React.ReactElement[];
  /**
   * 並び順を管理するための配列です。
   * これが存在しない場合、SortAreaは無効化されます。
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  list?: any[];
  /**
   * 並び順が変更された時に呼ばれるコールバックです。
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (keys: any[]) => void;
  /**
   * 先頭にソート不可な要素を追加します。
   */
  startAdornment?: React.ReactNode;
  /**
   * 末尾にソート不可な要素を追加します。
   */
  endAdornment?: React.ReactNode;
  /**
   * ソート機能を無効化します。
   */
  disabled?: boolean;
  /**
   * 複数選択モードを有効にします。
   * 有効にした状態でアイテムをクリックすると単独選択され、
   * Cmd/Ctrl + クリックで選択トグル、Shift + クリックで範囲選択ができます。
   * 選択中のアイテムをドラッグすると、選択された全アイテムがまとめて並び替えされます。
   */
  multiSelect?: boolean;
  /**
   * 現在選択中のアイテム (controlled mode 用)。`list` 内のアイテムと同じ参照を渡してください。
   * 未指定の場合は内部 state で管理されます。
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selected?: any[];
  /**
   * 選択状態が変わったときに呼ばれます。
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSelectionChange?: (selected: any[]) => void;
}

export const SortArea = ({
  /**
   * disabledの場合に不要なRowPropsとの差分のみ明示的に扱う。
   */
  list,
  startAdornment,
  endAdornment,
  children,
  onChange,
  disabled,
  multiSelect,
  selected,
  onSelectionChange,
  ...props
}: SortAreaProps) => {
  if (list === undefined || list.length === 0) {
    return null;
  }
  if (disabled) {
    return (
      <Row {...props}>
        {startAdornment}
        {children}
        {endAdornment}
      </Row>
    );
  }
  return (
    <SortAreaInner
      list={list}
      {...props}
      startAdornment={startAdornment}
      endAdornment={endAdornment}
      onChange={onChange}
      multiSelect={multiSelect}
      selected={selected}
      onSelectionChange={onSelectionChange}
    >
      {children}
    </SortAreaInner>
  );
};

interface SortAreaInnerProps extends SortAreaProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  list: any[];
}

const SortAreaInner = ({
  children,
  onChange,
  list: listProp,
  startAdornment,
  endAdornment,
  multiSelect,
  selected: selectedProp,
  onSelectionChange,
  ...props
}: SortAreaInnerProps) => {
  const cache = useRef(-1);
  const [_list, _setList] = useState(listProp);

  const list = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return arrayShallowEqual(_list, listProp) ? _list : listProp;
  }, [_list, listProp]);

  const setList = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (value: (current: any[]) => any[]) => {
      const next = value(list);
      // updater が同じ参照を返した場合は no-op として onChange も発火しない
      if (next === list) return;
      _setList(next);
      if (onChange) {
        onChange(next);
      }
    },
    [list, onChange],
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [internalSelected, setInternalSelected] = useState<any[]>([]);
  const selected = selectedProp ?? internalSelected;
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const anchorRef = useRef<any>(null);

  const updateSelected = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (next: any[]) => {
      if (selectedProp === undefined) {
        setInternalSelected(next);
      }
      onSelectionChange?.(next);
    },
    [selectedProp, onSelectionChange],
  );

  // list から消えたアイテムが選択集合に残らないように同期
  useEffect(() => {
    if (!multiSelect) return;
    if (selected.length === 0) return;
    const listSet = new Set(list);
    const filtered = selected.filter((item) => listSet.has(item));
    if (filtered.length !== selected.length) {
      if (anchorRef.current !== null && !listSet.has(anchorRef.current)) {
        anchorRef.current = null;
      }
      updateSelected(filtered);
    }
    // selected を deps に入れるとループするので意図的に list だけに依存
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, multiSelect]);

  const [isGroupDragging, setIsGroupDragging] = useState(false);
  const draggedIndexRef = useRef(-1);

  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.currentTarget.dataset.dragState = "dragging";
      e.dataTransfer.effectAllowed = "move";

      const currentIndex = parseInt(e.currentTarget.dataset[dataId]!, 10);
      draggedIndexRef.current = currentIndex;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const draggedItem = list[currentIndex];
      if (multiSelect && selectedSet.has(draggedItem) && selectedSet.size > 1) {
        setIsGroupDragging(true);

        // グループドラッグ中はカーソルに追従するゴーストにも件数バッジを付ける
        const original = e.currentTarget;
        const clone = original.cloneNode(true) as HTMLElement;
        clone.style.position = "absolute";
        clone.style.top = "-9999px";
        clone.style.left = "-9999px";
        clone.style.width = `${original.offsetWidth}px`;
        clone.style.pointerEvents = "none";
        // クローン側にデフォルトの dragging スタイルが適用されないよう剥がす
        delete clone.dataset.dragState;
        delete clone.dataset.selected;

        const badge = document.createElement("div");
        badge.textContent = String(selectedSet.size);
        badge.setAttribute("aria-hidden", "true");
        badge.style.cssText = [
          "position: absolute",
          "top: -8px",
          "right: -8px",
          "min-width: 24px",
          "height: 24px",
          "padding: 0 6px",
          "border-radius: 12px",
          "background: rgba(33, 150, 243, 1)",
          "color: white",
          "font-size: 12px",
          "font-weight: bold",
          "line-height: 24px",
          "text-align: center",
          "box-sizing: border-box",
          "box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3)",
        ].join(";");
        clone.appendChild(badge);

        document.body.appendChild(clone);
        e.dataTransfer.setDragImage(
          clone,
          e.nativeEvent.offsetX,
          e.nativeEvent.offsetY,
        );
        // ブラウザは setDragImage のスナップショットを同期で取得するため、
        // 次の tick で削除して DOM を汚さない
        setTimeout(() => clone.remove(), 0);
      }
    },
    [list, multiSelect, selectedSet],
  );

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    // dragging 中の要素 (= ドラッグ開始要素) は対象外
    if (e.currentTarget.dataset.dragState !== "dragging") {
      delete e.currentTarget.dataset.dropPosition;
    }
  }, []);

  const overRef = useRef<HTMLElement>(undefined);

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      if (e.currentTarget.draggable !== true) return;
      // ドラッグ中の要素 (グループドラッグの選択集合含む) には挿入線を出さない
      if (e.currentTarget.dataset.dragState === "dragging") return;

      const targetIndex = parseInt(e.currentTarget.dataset[dataId]!, 10);
      cache.current = targetIndex;

      // 直前まで over していた要素が別なら、そちらの挿入線を消す
      if (overRef.current && overRef.current !== e.currentTarget) {
        delete overRef.current.dataset.dropPosition;
      }

      // ドロップ後にドラッグアイテムが target のどちら側に来るかで線の位置を決める。
      // - グループドラッグ: 選択アイテムは常に target の手前に挿入される
      // - 単独 + 前方ドラッグ (dragged が target より前): splice の都合で target の後ろに入る
      // - 単独 + 後方ドラッグ (dragged が target より後): target の手前に入る
      const dropPosition = isGroupDragging
        ? "before"
        : draggedIndexRef.current < targetIndex
          ? "after"
          : "before";
      e.currentTarget.dataset.dropPosition = dropPosition;
      overRef.current = e.currentTarget;
    },
    [isGroupDragging],
  );
  const handleDragEnd = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      const currentIndex = parseInt(e.currentTarget.dataset[dataId]!, 10);
      delete e.currentTarget.dataset.dragState;
      if (overRef.current) {
        delete overRef.current.dataset.dropPosition;
      }
      draggedIndexRef.current = -1;
      setIsGroupDragging(false);
      e.dataTransfer.dropEffect = "move";

      setList((prev) => {
        const nextIndex = cache.current;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const draggedItem = prev[currentIndex];

        const isGroupMove =
          multiSelect && selectedSet.has(draggedItem) && selectedSet.size > 1;

        if (!isGroupMove) {
          const copy = prev.concat();
          const current = copy.splice(currentIndex, 1);
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          copy.splice(nextIndex, 0, ...current);
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return copy;
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const targetItem = prev[nextIndex];
        // 自分自身 (選択集合内) の上にドロップされた場合は何もしない
        if (selectedSet.has(targetItem)) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return prev;
        }

        const movingItems = prev.filter((item) => selectedSet.has(item));
        const remaining = prev.filter((item) => !selectedSet.has(item));
        const targetIndex = remaining.indexOf(targetItem);
        if (targetIndex === -1) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return prev;
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        remaining.splice(targetIndex, 0, ...movingItems);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return remaining;
      });
    },
    [setList, multiSelect, selectedSet],
  );

  const handleItemClick = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (e: React.MouseEvent<HTMLDivElement>, item: any) => {
      // e.target は Text ノード等 Element 以外になる可能性があるためガード
      if (
        e.target instanceof Element &&
        e.target.closest(INTERACTIVE_SELECTOR)
      ) {
        return;
      }

      const isCtrl = e.metaKey || e.ctrlKey;
      const isShift = e.shiftKey;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let next: any[];

      /* eslint-disable @typescript-eslint/no-unsafe-assignment */
      if (isShift && anchorRef.current !== null) {
        const anchorIdx = list.indexOf(anchorRef.current);
        const clickedIdx = list.indexOf(item);
        if (anchorIdx === -1 || clickedIdx === -1) {
          next = [item];
          anchorRef.current = item;
        } else {
          const [start, end] =
            anchorIdx <= clickedIdx
              ? [anchorIdx, clickedIdx]
              : [clickedIdx, anchorIdx];
          const mergedSet = new Set(selected);
          list.slice(start, end + 1).forEach((it) => mergedSet.add(it));
          next = list.filter((it) => mergedSet.has(it));
        }
      } else if (isCtrl) {
        if (selectedSet.has(item)) {
          next = selected.filter((it) => it !== item);
          if (anchorRef.current === item) {
            anchorRef.current = next.length > 0 ? next[next.length - 1] : null;
          }
        } else {
          next = [...selected, item];
          anchorRef.current = item;
        }
      } else {
        if (selectedSet.size === 1 && selectedSet.has(item)) {
          next = [];
          anchorRef.current = null;
        } else {
          next = [item];
          anchorRef.current = item;
        }
      }
      /* eslint-enable @typescript-eslint/no-unsafe-assignment */

      updateSelected(next);
    },
    [list, selected, selectedSet, updateSelected],
  );

  return (
    <Row
      {...props}
      css={css`
        & [draggable="true"] {
          z-index: 0;
          box-sizing: border-box;
          cursor: grab;
          user-select: none;
          transition:
            transform 0.2s ease-out,
            outline 0.2s ease-out,
            box-shadow 0.1s ease-out;

          &[data-drag-state="dragging"] {
            z-index: 999;
            cursor: grabbing;
            opacity: 0.5;
            transform: scale(0.8);
          }

          &[data-selected="true"] {
            outline: 2px solid rgba(33, 150, 243, 0.6);
          }

          /* ドロップ位置の挿入線。スワップではなく要素間に入ることが伝わるよう、
             target の前/後 (左右) に縦線を表示する */
          &[data-drop-position="before"] {
            box-shadow: -3px 0 0 0 rgba(33, 150, 243, 1);
          }
          &[data-drop-position="after"] {
            box-shadow: 3px 0 0 0 rgba(33, 150, 243, 1);
          }
        }
      `}
    >
      {startAdornment}
      {React.Children.map(children, (child, index: number) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const item = list[index];
        // list より子要素が多いケース (例: アップロード圧縮中の一時アイテムを末尾に含む)
        // では、対応する list の要素が無いアイテムは選択対象から除外する。
        const isSelectable = multiSelect && index < list.length;
        const isSelected = isSelectable && selectedSet.has(item);
        const isInGroupDrag = isGroupDragging && isSelected;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
        const childProps = child.props as any;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const childOnClick = childProps.onClick as
          | React.MouseEventHandler<HTMLDivElement>
          | undefined;
        const mergedOnClick = isSelectable
          ? (e: React.MouseEvent<HTMLDivElement>) => {
              childOnClick?.(e);
              handleItemClick(e, item);
            }
          : childOnClick;

        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        return React.cloneElement(child, {
          onDragStart: handleDragStart,
          onDragOver: handleDragOver,
          onDragEnd: handleDragEnd,
          onDragLeave: handleDragLeave,
          draggable: true,
          [dataid]: index,
          ...childProps,
          ...(isSelectable
            ? {
                onClick: mergedOnClick,
                "data-selected": isSelected ? "true" : undefined,
                "aria-selected": isSelected,
              }
            : {}),
          // グループドラッグ中は、選択された全アイテムに dragging 視覚効果を適用する
          ...(isInGroupDrag ? { "data-drag-state": "dragging" } : {}),
        });
      })}
      {endAdornment}
    </Row>
  );
};
